import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { en } from '../locales/en';

type TranslationType = typeof en;

interface LocalizationContextType {
  t: TranslationType & ((key: string) => string);
  language: string;
  setLanguage: (lang: string) => void;
}

const LocalizationContext = createContext<LocalizationContextType>({
  t: Object.assign((key: string) => key, en),
  language: 'en',
  setLanguage: () => { },
});

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

export const LocalizationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t: translate } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  // Create a function that can also act as an object
  const translationFunction = (key: string) => translate(key);

  // Add nested objects to the function
  const translationProxy = new Proxy(translationFunction as any, {
    get: (target, prop: string) => {
      if (prop === 'apply' || prop === 'call' || prop === 'bind') {
        return Reflect.get(target, prop);
      }
      return new Proxy({}, {
        get: (_, nestedProp: string) => {
          return new Proxy({}, {
            get: (__, finalProp: string) => {
              return translate(`${prop}.${nestedProp}.${finalProp}`);
            }
          });
        }
      });
    }
  });

  return (
    <LocalizationContext.Provider
      value={{
        t: translationProxy,
        language,
        setLanguage: handleLanguageChange,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};
